import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import {
  Box,
  Checkbox,
  ErrorModal,
  Text,
  TextButton,
  Toast,
  Tooltip,
  useOpenModal,
} from '@modusign/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useSelectionValues, UseSelectionValuesResult } from '@hooks';
import LoadingStatus from '@libs/loading-status';

import BatchActionToolbar from '@app/components/BatchActionToolbar';
import EmptyListLayout from '@app/components/EmptyListLayout';
import RootLoadingSpinnerComponent from '@app/components/Loading/RootLoadingSpinnerComponent';
import SearchArea from '@app/components/SearchArea';
import SelectLimit from '@app/components/SelectLimit';
import SwitchablePagination from '@app/components/SwitchablePagination';
import { getIsConvertingDraftToPdf, useCopyDraft } from '@app/features/form';
import DeleteDraftsModal from '@app/features/form/components/DeleteDraftsModal';
import { useDraftPermission } from '@app/features/form/hooks/useDraftPermission';
import useDrafts from '@app/features/form/hooks/useDrafts';
import { useCreateTemplateAvailability } from '@app/features/template';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import josa from '@app/libs/josa';
import { getDocumentUploadStatus } from '@app/store/documents/selectors';
import { GetDraftsResponse } from '@app/store/transport-layer/form/drafts';

import DraftListItem from './DraftListItem';

interface DraftsPageContentProps {
  filter: 'all' | 'onlyMine';
}

export default function DraftsPageContent({ filter }: DraftsPageContentProps) {
  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [openedDraft, setOpenedDraft] = useState('');

  const selectedDraftIds = useSelectionValues<string>();

  const clearSelected = selectedDraftIds.clear;
  useEffect(() => {
    // 전체, 내가 등록 필터가 바뀌면 선택 상태를 초기화합니다. [QA-996]
    clearSelected();
  }, [clearSelected, filter]);

  const workspaceId = useWorkspaceId();
  const {
    isLoading: isDraftsLoading,
    isError: isDraftsError,
    data: drafts,
    refetch: refetchDrafts,
  } = useDrafts(workspaceId, {
    limit,
    page,
    keyword: searchKeyword || undefined,
    assigned: filter !== 'all',
  });

  const { isLoading: isTemplatesLoading } = useCreateTemplateAvailability();

  const {
    isLoading: isDraftCopying,
    mutate: copyDraft,
    error: copyDraftError,
    reset: resetCopyDraft,
  } = useCopyDraft({
    onSuccess: () => {
      Toast.success({ description: '복사본 생성을 완료했습니다.' });
    },
  });

  const handleDraftCopy = (workspaceId: string, draftId: string) => {
    copyDraft({ workspaceId, id: draftId });
  };

  const isConvertingToPdf = useSelector(getIsConvertingDraftToPdf);
  const documentUploadStatusForSigningRequest = useSelector(getDocumentUploadStatus);

  const isLoading =
    isDraftsLoading ||
    isTemplatesLoading ||
    isConvertingToPdf ||
    documentUploadStatusForSigningRequest === LoadingStatus.PENDING ||
    isDraftCopying;

  if (isLoading || !workspaceId) {
    return <RootLoadingSpinnerComponent isLoading />;
  }

  return (
    <div className="Container Container--min-md Container--min-auto-tablet">
      <Box px={[0, null, 7]} py={[0, null, 4]}>
        <Box my={7} width={['100%', null, '300px']}>
          <SearchArea
            placeholder="서식 이름으로 찾기"
            value={searchDisplayValue}
            onChange={(v) => setSearchDisplayValue(v)}
            onSubmit={(v) => {
              setPage(1);
              setSearchKeyword(v);
            }}
          />
        </Box>
        <Box role="table" sx={{ transition: '0.3s 0s' }} width="100%">
          {selectedDraftIds.count ? (
            <DraftsBatchActionToolbar
              drafts={drafts?.drafts ?? []}
              selectedDraftIds={selectedDraftIds}
            />
          ) : (
            <TableHeader drafts={drafts?.drafts ?? []} selectedDraftIds={selectedDraftIds} />
          )}
          <Box borderBottom="1px solid" borderColor="tableBorderNeutralBase" />
          {(drafts?.drafts.length ?? 0) > 0 && (
            <div role="rowgroup">
              {drafts?.drafts.map((draft) => (
                <DraftListItem
                  createdAt={draft.createdAt}
                  disabled={false}
                  id={draft.id}
                  isChecked={selectedDraftIds.selected.includes(draft.id)}
                  isOpen={openedDraft === draft.id}
                  key={draft.id}
                  name={draft.name}
                  updatedAt={draft.updatedAt}
                  onCheckToggle={() => selectedDraftIds.toggle(draft.id)}
                  onCopy={() => handleDraftCopy(workspaceId, draft.id)}
                  onToggleDropdown={() => setOpenedDraft((id) => (id === draft.id ? '' : draft.id))}
                />
              ))}
            </div>
          )}
        </Box>
        {(drafts?.drafts.length ?? 0) > 0 && (
          <Box mt="36px" position="relative" pt={[9, null, 0]}>
            <SwitchablePagination
              currentPage={page}
              maxPage={drafts?.count ? Math.floor((drafts.count - 1) / limit) + 1 : 1}
              onClick={setPage}
            />
            <Box
              position="absolute"
              right={0}
              sx={{
                transform: 'translateY(-50%)',
              }}
              top={[0, null, '50%']}
              width="120px"
            >
              <SelectLimit
                className="EditingDocumentsPage__count-per-page-selector"
                limit={limit}
                onChange={(v: number) => {
                  setPage(1);
                  setLimit(v);
                }}
              />
            </Box>
          </Box>
        )}
        {drafts?.drafts.length === 0 && !searchKeyword && (
          <EmptyListLayout>
            <Text as="p" color="textNeutralLight">
              작성된 서식이 없습니다.
            </Text>
          </EmptyListLayout>
        )}
        {drafts?.drafts.length === 0 && searchKeyword && (
          <EmptyListLayout>
            <div>
              <Text fontWeight="medium">{searchKeyword}</Text>
              <Text>{josa.only(searchKeyword, '으로/로')} 검색한 결과가 없습니다.</Text>
            </div>
            <TextButton onClick={() => setSearchKeyword('')}>검색 초기화하기</TextButton>
          </EmptyListLayout>
        )}
        {isDraftsError && (
          <EmptyListLayout>
            <Text display="block">서식 목록을 불러오지 못했습니다.</Text>
            <TextButton
              onClick={() => {
                refetchDrafts();
              }}
            >
              다시 시도하기
            </TextButton>
          </EmptyListLayout>
        )}
      </Box>
      <ErrorModal error={copyDraftError} isOpen title="서식 복사 실패" onClose={resetCopyDraft} />
    </div>
  );
}

function TableHeader({
  drafts,
  selectedDraftIds,
}: {
  drafts: GetDraftsResponse['drafts'];
  selectedDraftIds: UseSelectionValuesResult<string>;
}) {
  const { manageDraftPermission } = useDraftPermission();

  const isAllChecked = useMemo(() => {
    if (!drafts.length) {
      return false;
    }
    return drafts.every((d) => selectedDraftIds.selected.includes(d.id));
  }, [drafts, selectedDraftIds]);

  return (
    <Box display={['none', 'block']} role="rowgroup">
      <Text
        as="div"
        color="textNeutralVeryLight"
        display="flex"
        fontSize="sm"
        py={4}
        role="row"
        sx={{
          borderBottom: '1px solid',
          borderColor: 'tableBorderNeutralBase',
        }}
      >
        <Box
          alignItems="center"
          display={['none', 'flex']}
          justifyContent="center"
          role="columnheader"
          width="48px"
        >
          <Checkbox
            checked={isAllChecked}
            disabled={!manageDraftPermission.isPermitted}
            onClick={() =>
              selectedDraftIds.set(
                drafts.map((d) => d.id),
                !isAllChecked
              )
            }
          />
        </Box>
        <Box flexGrow={1} role="columnheader">
          서식 제목
        </Box>
        <Box role="columnheader" textAlign="center" width="120px">
          최초 작성 시각
        </Box>
        <Box role="columnheader" textAlign="center" width="120px">
          마지막 저장 시각
        </Box>
        <Box role="columnheader" textAlign="center" width="120px">
          추가 메뉴
        </Box>
      </Text>
    </Box>
  );
}

function DraftsBatchActionToolbar({
  drafts,
  selectedDraftIds,
}: {
  drafts: GetDraftsResponse['drafts'];
  selectedDraftIds: UseSelectionValuesResult<string>;
}) {
  const openModal = useOpenModal();
  const { manageDraftPermission } = useDraftPermission();

  const isAllChecked = useMemo(() => {
    if (!drafts.length) {
      return false;
    }
    return drafts.every((d) => selectedDraftIds.selected.includes(d.id));
  }, [drafts, selectedDraftIds]);

  return (
    <BatchActionToolbar
      checkbox={
        <Checkbox
          checked={isAllChecked}
          indeterminate={!!selectedDraftIds.count && !isAllChecked}
          onClick={() =>
            selectedDraftIds.set(
              drafts.map((d) => d.id),
              !isAllChecked
            )
          }
        />
      }
      selectedItemsCount={selectedDraftIds.count}
    >
      <Tooltip
        content={manageDraftPermission.message}
        disabled={manageDraftPermission.isPermitted}
        scale="sm"
      >
        <BatchActionToolbar.Button
          disabled={!manageDraftPermission.isPermitted}
          leftIcon={faTrashCan}
          onClick={() =>
            openModal(({ isOpen, onClose }) => (
              <DeleteDraftsModal
                draftIds={selectedDraftIds.selected}
                isOpen={isOpen}
                onClose={onClose}
                onConfirm={() => selectedDraftIds.clear()}
              />
            ))
          }
        >
          삭제
        </BatchActionToolbar.Button>
      </Tooltip>
    </BatchActionToolbar>
  );
}
